import { ValidationRule, ValidationResult } from '../types';

// Language-specific validation functions
export const pythonValidators = {
  require_print: (code: string): boolean => {
    const result = /print\s*\(/.test(code);
    return result;
  },
  require_if: (code: string): boolean => /\bif\s+/.test(code),
  require_elif: (code: string): boolean => /\belif\s+/.test(code),
  require_else: (code: string): boolean => /\belse\s*:/.test(code),
  require_while: (code: string): boolean => /\bwhile\s+/.test(code),
  require_for: (code: string): boolean => /\bfor\s+/.test(code),
  require_random_module: (code: string): boolean => /import\s+random/.test(code),
  require_randint_function: (code: string): boolean => /random\.randint\s*\(/.test(code),
  require_arithmetic_ops: (code: string): boolean => /[+\-*/]/.test(code),
  require_function_def: (code: string): boolean => /\bdef\s+\w+\s*\(/.test(code),
  require_list: (code: string): boolean => /\[.*\]/.test(code),
  require_dict: (code: string): boolean => /\{.*\}/.test(code),
  require_import_math: (code: string): boolean => /import\s+math/.test(code),
  require_try_except: (code: string): boolean => /\btry\s*:/.test(code) && /\bexcept\s+/.test(code),
  require_class_def: (code: string): boolean => /\bclass\s+\w+/.test(code),
  require_lambda: (code: string): boolean => /\blambda\s+/.test(code),
  require_list_comprehension: (code: string): boolean => /\[.*for.*in.*\]/.test(code),
  require_string_formatting: (code: string): boolean => /\.format\s*\(|f["']/.test(code),
  require_file_operations: (code: string): boolean => /open\s*\(/.test(code),
  require_os_module: (code: string): boolean => /import\s+os/.test(code),
};

export const javascriptValidators = {
  require_console_log: (code: string): boolean => /console\.log\s*\(/.test(code),
  require_if: (code: string): boolean => /\bif\s*\(/.test(code),
  require_else: (code: string): boolean => /\belse\s*{/.test(code),
  require_else_if: (code: string): boolean => /\belse\s+if\s*\(/.test(code),
  require_while: (code: string): boolean => /\bwhile\s*\(/.test(code),
  require_for: (code: string): boolean => /\bfor\s*\(/.test(code),
  require_for_in: (code: string): boolean => /\bfor\s*\([^)]*\bin\s+/.test(code),
  require_for_of: (code: string): boolean => /\bfor\s*\([^)]*\bof\s+/.test(code),
  require_math_module: (code: string): boolean => /Math\./.test(code),
  require_math_random: (code: string): boolean => /Math\.random\s*\(/.test(code),
  require_arithmetic_ops: (code: string): boolean => /[+\-*/]/.test(code),
  require_function_def: (code: string): boolean => /function\s+\w+\s*\(|const\s+\w+\s*=\s*\(|let\s+\w+\s*=\s*\(|var\s+\w+\s*=\s*\(/.test(code),
  require_arrow_function: (code: string): boolean => /=>\s*/.test(code),
  require_array: (code: string): boolean => /\[.*\]/.test(code),
  require_object: (code: string): boolean => /\{.*\}/.test(code),
  require_try_catch: (code: string): boolean => /\btry\s*{/.test(code) && /\bcatch\s*\(/.test(code),
  require_class_def: (code: string): boolean => /\bclass\s+\w+/.test(code),
  require_async_function: (code: string): boolean => /\basync\s+/.test(code),
  require_promise: (code: string): boolean => /Promise\./.test(code),
  require_fetch: (code: string): boolean => /fetch\s*\(/.test(code),
  require_dom_manipulation: (code: string): boolean => /document\.|getElementById|querySelector/.test(code),
  require_json: (code: string): boolean => /JSON\./.test(code),
  require_localStorage: (code: string): boolean => /localStorage\./.test(code),
  require_setTimeout: (code: string): boolean => /setTimeout\s*\(/.test(code),
  require_setInterval: (code: string): boolean => /setInterval\s*\(/.test(code),
};

// Helper functions for pattern validation
export const validateSyntaxPattern = (code: string, pattern: string): boolean => {
  try {
    const regex = new RegExp(pattern, 'gi');
    return regex.test(code);
  } catch (error) {
    console.warn('Invalid regex pattern:', pattern);
    return false;
  }
};

export const validateImportPattern = (code: string, pattern: string, language: 'python' | 'javascript'): boolean => {
  if (language === 'python') {
    // Python import patterns
    if (pattern.startsWith('import ')) {
      const moduleName = pattern.replace('import ', '').trim();
      return new RegExp(`import\\s+${moduleName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi').test(code);
    } else if (pattern.startsWith('from ')) {
      return new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi').test(code);
    }
  } else {
    // JavaScript import patterns
    if (pattern.startsWith('import ')) {
      return new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi').test(code);
    } else if (pattern.startsWith('require(')) {
      return new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi').test(code);
    }
  }
  return false;
};

export const validateFunctionPattern = (code: string, pattern: string, language: 'python' | 'javascript'): boolean => {
  if (language === 'python') {
    // Python function patterns
    if (pattern.includes('def ')) {
      const functionName = pattern.replace('def ', '').replace('():', '').trim();
      return new RegExp(`def\\s+${functionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(`, 'gi').test(code);
    }
  } else {
    // JavaScript function patterns
    if (pattern.includes('function ')) {
      const functionName = pattern.replace('function ', '').replace('()', '').trim();
      return new RegExp(`function\\s+${functionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\(`, 'gi').test(code);
    }
  }
  return false;
};

// Main validation engine
export const validateCodeWithRules = (
  code: string,
  rules: ValidationRule[],
  language: 'python' | 'javascript',
  consoleOutput: string[] = []
): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  const validators = language === 'python' ? pythonValidators : javascriptValidators;
  
  rules.forEach(rule => {
    // Skip rules for other languages
    if (rule.language !== language) return;
    
    let passed = false;
    
    switch (rule.type) {
      case 'pattern':
        // Use predefined validators
        const validator = validators[rule.condition as keyof typeof validators];
        if (validator) {
          passed = validator(code);
        } else {
          console.warn(`Unknown validation pattern: ${rule.condition} for language: ${language}`);
        }
        break;
        
      case 'syntax':
        // Syntax-specific validation using regex patterns
        passed = validateSyntaxPattern(code, rule.condition);
        break;
        
      case 'output':
        // Check console output
        passed = consoleOutput.some(output => 
          output.toLowerCase().includes(rule.condition.toLowerCase())
        );
        break;
        
      case 'import':
        // Check for imports
        passed = validateImportPattern(code, rule.condition, language);
        break;
        
      case 'function':
        // Check for function usage
        passed = validateFunctionPattern(code, rule.condition, language);
        break;
    }
    
    if (!passed && rule.required) {
      if (rule.severity === 'error') {
        errors.push(rule.message);
      } else {
        warnings.push(rule.message);
      }
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

// Legacy validation function for backward compatibility
export const validateCode = (
  code: string,
  rules: ValidationRule[],
  consoleOutput: string[] = []
): { isValid: boolean; errors: string[]; warnings: string[] } => {
  // Try to detect language from code patterns
  let language: 'python' | 'javascript' = 'python';
  
  if (code.includes('console.log') || code.includes('function ') || code.includes('const ') || code.includes('let ')) {
    language = 'javascript';
  }
  
  return validateCodeWithRules(code, rules, language, consoleOutput);
};
